import { Vec2 } from './math';
import type { HatchEntity } from './types';

export interface HatchLineDef {
  /** Radians. */
  angle: number;
  /** Millimetres between lines, before the hatch scale is applied. */
  spacing: number;
  /** Millimetres along the line direction. */
  offset?: number;
  /** Optional dash pattern in millimetres. */
  dash?: number[];
}

export interface HatchPattern {
  id: string;
  name: string;
  description: string;
  lines: HatchLineDef[];
}

const HALF_PI = Math.PI / 2;
const QUARTER = Math.PI / 4;

export const HATCH_PATTERNS: Record<string, HatchPattern> = {
  ANSI31: {
    id: 'ANSI31',
    name: 'ANSI31',
    description: 'Iron / general section, 45°',
    lines: [{ angle: QUARTER, spacing: 3.175 }],
  },
  ANSI32: {
    id: 'ANSI32',
    name: 'ANSI32',
    description: 'Steel, 45° double',
    lines: [
      { angle: QUARTER, spacing: 6.35 },
      { angle: QUARTER, spacing: 6.35, offset: 1.6 },
    ],
  },
  ANSI33: {
    id: 'ANSI33',
    name: 'ANSI33',
    description: 'Bronze / brass, 45° dashed',
    lines: [{ angle: QUARTER, spacing: 3.175, dash: [4, 2] }],
  },
  ANSI34: {
    id: 'ANSI34',
    name: 'ANSI34',
    description: 'Plastic / rubber, 45° wide',
    lines: [{ angle: QUARTER, spacing: 6.35 }],
  },
  ANSI37: {
    id: 'ANSI37',
    name: 'ANSI37',
    description: 'Cross hatch, 45° / 135°',
    lines: [
      { angle: QUARTER, spacing: 3.175 },
      { angle: -QUARTER, spacing: 3.175 },
    ],
  },
  GRID: {
    id: 'GRID',
    name: 'GRID',
    description: 'Orthogonal grid',
    lines: [
      { angle: 0, spacing: 3 },
      { angle: HALF_PI, spacing: 3 },
    ],
  },
  CONCRETE: {
    id: 'CONCRETE',
    name: 'CONCRETE',
    description: 'Concrete, 45° with paired lines',
    lines: [
      { angle: QUARTER, spacing: 5 },
      { angle: -QUARTER, spacing: 5, dash: [1.2, 2.4] },
    ],
  },
};

export const DEFAULT_HATCH_PATTERN = 'ANSI31';

export function resolvePattern(idOrName: string): HatchPattern {
  const key = idOrName.trim().toUpperCase();
  return HATCH_PATTERNS[key] ?? HATCH_PATTERNS[DEFAULT_HATCH_PATTERN]!;
}

export interface HatchOptions {
  angle?: number;
  scale?: number;
  origin?: Vec2;
  maxSegments?: number;
}

interface LoopEdge {
  a: Vec2;
  b: Vec2;
}

/**
 * Even-odd scanline hatch of one or more loops. Returns pairs of world points,
 * each pair being one hatch line; dashes are already split.
 */
export function hatchFromLoops(
  loops: readonly Vec2[][],
  pattern: HatchPattern,
  options: HatchOptions = {},
): Vec2[][] {
  const angle = options.angle ?? 0;
  const scale = Math.max(Math.abs(options.scale ?? 1), 1e-4);
  const origin = options.origin ?? Vec2.of(0, 0);
  const maxSegments = options.maxSegments ?? 20000;

  const out: Vec2[][] = [];
  if (loops.length === 0 || pattern.lines.length === 0) return out;

  for (const lineDef of pattern.lines) {
    const totalAngle = angle + lineDef.angle;
    const cos = Math.cos(-totalAngle);
    const sin = Math.sin(-totalAngle);

    const toPatternSpace = (p: Vec2): Vec2 => {
      const dx = p.x - origin.x;
      const dy = p.y - origin.y;
      return Vec2.of(dx * cos - dy * sin, dx * sin + dy * cos);
    };
    const toWorld = (p: Vec2): Vec2 => {
      const c = Math.cos(totalAngle);
      const s = Math.sin(totalAngle);
      return Vec2.of(origin.x + p.x * c - p.y * s, origin.y + p.x * s + p.y * c);
    };

    const edges: LoopEdge[] = [];
    let minY = Infinity;
    let maxY = -Infinity;
    for (const loop of loops) {
      if (loop.length < 3) continue;
      for (let i = 0; i < loop.length; i++) {
        const a = toPatternSpace(loop[i]!);
        const b = toPatternSpace(loop[(i + 1) % loop.length]!);
        if (Math.abs(a.y - b.y) < 1e-12) continue;
        edges.push({ a, b });
        minY = Math.min(minY, a.y, b.y);
        maxY = Math.max(maxY, a.y, b.y);
      }
    }
    if (edges.length === 0) continue;

    const spacing = Math.max(lineDef.spacing * scale, 1e-3);
    const offset = (lineDef.offset ?? 0) * scale;
    const startIndex = Math.ceil((minY - offset) / spacing);
    const endIndex = Math.floor((maxY - offset) / spacing);

    for (let i = startIndex; i <= endIndex; i++) {
      const y = i * spacing + offset;
      const xs: number[] = [];
      for (const edge of edges) {
        const { a, b } = edge;
        const lowY = Math.min(a.y, b.y);
        const highY = Math.max(a.y, b.y);
        if (y < lowY || y >= highY) continue;
        const t = (y - a.y) / (b.y - a.y);
        xs.push(a.x + (b.x - a.x) * t);
      }
      if (xs.length < 2) continue;
      xs.sort((p, q) => p - q);
      for (let k = 0; k + 1 < xs.length; k += 2) {
        const from = toWorld(Vec2.of(xs[k]!, y));
        const to = toWorld(Vec2.of(xs[k + 1]!, y));
        if (from.distanceTo(to) < 1e-6) continue;
        if (lineDef.dash && lineDef.dash.length > 0) {
          pushDashed(out, from, to, lineDef.dash.map((d) => d * scale));
        } else {
          out.push([from, to]);
        }
        if (out.length >= maxSegments) return out;
      }
    }
  }

  return out;
}

/** Convenience wrapper used by the tessellator and the hatch tool. */
export function generateHatch(hatch: HatchEntity): Vec2[][] {
  return hatchFromLoops(hatch.loops, resolvePattern(hatch.pattern), {
    angle: hatch.angle,
    scale: hatch.scale,
    origin: hatch.origin,
  });
}

function pushDashed(out: Vec2[][], from: Vec2, to: Vec2, pattern: number[]): void {
  const length = from.distanceTo(to);
  if (length < 1e-9) return;
  const dir = to.sub(from).div(length);
  let offset = 0;
  let index = 0;
  let drawing = true;
  while (offset < length) {
    const step = Math.min(pattern[index % pattern.length]!, length - offset);
    const a = from.add(dir.scale(offset));
    const b = from.add(dir.scale(offset + step));
    if (drawing) out.push([a, b]);
    offset += step;
    index += 1;
    drawing = !drawing;
    if (pattern.every((p) => p <= 0)) break;
  }
}
